/* eslint-disable class-methods-use-this */
import GroupApi from "../API/group"
import { BarsConfig } from "../models/bars"
import { Group } from "../models/group"
import { Module } from "../models/module"
import { Service } from "./Service"
import StoreService from "./store"
import UserService from "./user"

class GroupService extends Service {
    private static instance: GroupService

    constructor() {
        super()
        // set le nom du service
        this.name = "GroupService"
        // ajoute toute les methodes du service au tableau de mapping
        // this.serviceMap = []
        this.serviceMap.push(
            { key: "getGroups", fct: this.getGroups },
            { key: "updateSettings", fct: this.updateSettings },
            { key: "getSetting", fct: this.getSetting },
            { key: "setBarConfig", fct: this.setBarConfig },
            { key: "getModules", fct: this.getModules },
            { key: "getGroupDetails", fct: this.getGroupDetails },
            { key: "getByOwner", fct: this.getByOwner }
        )
    }

    /**
     * Récuperation de l'instance
     */
    public static getInstance(): GroupService {
        if (!this.instance) {
            this.instance = new GroupService()
        }
        return this.instance
    }

    /**
     * getGroups lance API REST de récupérer tous les groups
     * @param showSuper affiche le supergroup (default false)
     */
    public async getGroups(showSuper: boolean = false): Promise<Group[]> {
        return GroupApi.getGroups(showSuper)
    }
    /**
     * Ajouter le groupe superviseurs
     * @param modules liste  des id des modules
     * @param defaultGroupSettings paramètre à mettre dans le group du superviseur
     */
    public async addSupervisorGroup(
        modules: Array<string>,
        defaultGroupSettings?: any
    ): Promise<any | undefined> {
        const groupData: any = {
            name: "superviseur",
            displayName: "Superviseur",
            modules,
            settings: defaultGroupSettings,
        }
        const newGroup = await GroupApi.createGroup(groupData)

        return newGroup
    }
    /**
     * retourne les détails d'un groupe
     * @param groupId
     * @returns Promise de group
     */
    public async getGroupDetails(groupId: string): Promise<Group> {
        if (groupId) {
            const group = await GroupApi.getGroup(groupId)
            return group
        }
        throw Error("Impossible to get group")
    }
    /**
     * getBarConfig lance API REST de getBarConfig afin de pouvoir récupérer
     * les configurations de la bar de chaque groupe
     */
    public async getBarConfig(): Promise<BarsConfig | undefined> {
        let barConfig: any
        const groupId = await UserService.getInstance().getConnectedUserGroup()

        // Si on a des groupes ou l'user
        if (groupId) {
            // Si la config de la bar est trouvé dans la session on la récupère
            if (StoreService.getInstance().local.getSession(`barConfig`))
                return StoreService.getInstance().local.getSession(`barConfig`)

            barConfig = await GroupApi.getSetting(groupId, "bar")
            StoreService.getInstance().local.setSession(`barConfig`, barConfig)
        }
        return barConfig
    }

    /**
     * getSetting récupére le setting du type demandé
     * @param attr du setting que l'on veut avoir
     * @param groupId dans quel group on veut obtenir le setting
     */
    public async getSetting(type: string, groupId?: string): Promise<any> {
        let settings: any
        const currentGroupId =
            groupId || (await UserService.getInstance().getConnectedUserGroup())
        if (currentGroupId) {
            settings = await GroupApi.getSetting(currentGroupId, type)
        }
        return settings
    }

    /**
     * getModules récupére les modules du groupe
     * @param groupId dans quel group on veut obtenir les modules
     */
    public async getModules(groupId: string): Promise<Array<Module>> {
        if (groupId) {
            const modules = await GroupApi.getModules(groupId)
            return modules
        }
        throw Error("Impossible to get Modules")
    }

    /**
     * setBarConfig mise à jour des config de la barre pour le groupe
     * @param attr du setting que l'on veut avoir
     * @param groupId dans quel group on veut obtenir le setting
     */
    public async setBarConfig(
        primaryBgd?: string,
        secondaryBgd?: string,
        primaryColor?: string,
        secondaryColor?: string
    ): Promise<string | undefined> {
        // version simplifié des settings de la bar
        const setting: {
            primaryBgd?: string
            secondaryBgd?: string
            primaryColor?: string
            secondaryColor?: string
        } = {
            primaryBgd,
            secondaryBgd,
            primaryColor,
            secondaryColor,
        }
        const currentGroupId =
            await UserService.getInstance().getConnectedUserGroup()
        if (currentGroupId) {
            const id = await GroupApi.setBarConfig(setting, currentGroupId)
            return id
        }
        return undefined
    }

    /**
     * updateSettings lance API REST de update group settings afin de pouvoir modifier
     * les paramétres de groupes de l'utilisateur connecté
     * @param settings la nouvelle valeur du setting
     * @param type le type de setting qu'on veut modifier (attr de l'obj setting)
     */
    public async updateSettings(
        setting: unknown,
        type: string,
        group?: string
    ): Promise<any> {
        const groupId =
            group || (await UserService.getInstance().getConnectedUserGroup())
        if (groupId) {
            const params: any = {}
            params[type] = setting
            return GroupApi.updateSettings(groupId, params, type)
        }
        return Promise.reject(Error("Pas de group pour cet utilisateur"))
    }

    public async getByOwner(owner?: string): Promise<any> {
        return GroupApi.getByOwner(owner)
    }

    public async getAvailableGroups(userId: string): Promise<any> {
        return GroupApi.getAvailableGroups(userId)
    }
}

export default GroupService
